// InsightCards — row of 1-3 AI insight cards from dash_ai_insights.
// Footer includes a confidence sparkline over the last ~14 runs (lazy-loaded).
// Each card: type badge, title, summary, expandable detail.

import { useState } from "react";
import type { InsightCard } from "../../../../worker/ai/types";
import InsightSparkline from "./InsightSparkline";

// ── Insight type configuration ────────────────────────────────────────────────

type InsightType = InsightCard["insight_type"];

interface TypeConfig {
  label: string;
  bgClass: string;
  textClass: string;
  icon: string; // SVG path data
}

const TYPE_CONFIG: Record<InsightType, TypeConfig> = {
  recommendation: {
    label: "Recommendation",
    bgClass: "bg-teal-100 dark:bg-teal-900/30",
    textClass: "text-teal-700 dark:text-teal-300",
    icon: "M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z",
  },
  alert: {
    label: "Alert",
    bgClass: "bg-amber-100 dark:bg-amber-900/30",
    textClass: "text-amber-700 dark:text-amber-300",
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  },
  trend: {
    label: "Trend",
    bgClass: "bg-blue-100 dark:bg-blue-900/30",
    textClass: "text-blue-700 dark:text-blue-300",
    icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
  },
  milestone: {
    label: "Milestone",
    bgClass: "bg-green-100 dark:bg-green-900/30",
    textClass: "text-green-700 dark:text-green-300",
    icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 0 0 1.946-.806 3.42 3.42 0 0 1 4.438 0 3.42 3.42 0 0 0 1.946.806 3.42 3.42 0 0 1 3.138 3.138 3.42 3.42 0 0 0 .806 1.946 3.42 3.42 0 0 1 0 4.438 3.42 3.42 0 0 0-.806 1.946 3.42 3.42 0 0 1-3.138 3.138 3.42 3.42 0 0 0-1.946.806 3.42 3.42 0 0 1-4.438 0 3.42 3.42 0 0 0-1.946-.806 3.42 3.42 0 0 1-3.138-3.138 3.42 3.42 0 0 0-.806-1.946 3.42 3.42 0 0 1 0-4.438 3.42 3.42 0 0 0 .806-1.946 3.42 3.42 0 0 1 3.138-3.138z",
  },
  risk: {
    label: "Risk",
    bgClass: "bg-red-100 dark:bg-red-900/30",
    textClass: "text-red-700 dark:text-red-300",
    icon: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  },
};

// ── Individual insight card ───────────────────────────────────────────────────

function InsightCardItem({ card }: { card: InsightCard }) {
  const [expanded, setExpanded] = useState(false);
  const type =
    card.insight_type in TYPE_CONFIG ? card.insight_type : "recommendation";
  const config = TYPE_CONFIG[type];

  const confidencePct = Math.round((card.confidence ?? 0) * 100);
  const generatedDate = card.generated_at
    ? new Date(card.generated_at).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-2 transition-all"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      {/* Type badge + title row */}
      <div className="flex items-start gap-2">
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${config.bgClass} ${config.textClass}`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d={config.icon} />
          </svg>
          {config.label}
        </span>
        {generatedDate && (
          <span
            className="text-xs ml-auto flex-shrink-0"
            style={{ color: "var(--text-muted)" }}
          >
            {generatedDate}
          </span>
        )}
      </div>

      <h4
        className="text-sm font-semibold leading-snug"
        style={{ color: "var(--text-primary)" }}
      >
        {card.title}
      </h4>

      <p
        className="text-xs leading-relaxed"
        style={{ color: "var(--text-secondary)" }}
      >
        {card.summary}
      </p>

      {/* Expand/collapse */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="text-xs font-medium text-left hover:underline mt-1 flex items-center gap-1"
        style={{ color: "var(--text-muted)" }}
      >
        {expanded ? "Show less" : "Show more"}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${expanded ? "rotate-180" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div
          className="pt-2 space-y-2 text-xs"
          style={{
            borderTop: "1px solid var(--border-subtle)",
            color: "var(--text-secondary)",
          }}
        >
          <div>
            <p
              className="font-semibold mb-0.5"
              style={{ color: "var(--text-primary)" }}
            >
              Recommended Action
            </p>
            <p className="leading-relaxed">{card.action}</p>
          </div>
          <div>
            <p
              className="font-semibold mb-0.5"
              style={{ color: "var(--text-primary)" }}
            >
              Evidence
            </p>
            <p className="leading-relaxed">{card.evidence}</p>
          </div>
          <div className="flex items-center justify-between pt-1">
            <div
              className="flex items-center gap-2"
              style={{ color: "var(--text-muted)" }}
            >
              <span>Confidence: {confidencePct}%</span>
              <InsightSparkline insightType={type} />
            </div>
            {card.model_used && (
              <span
                className="font-mono"
                style={{ color: "var(--text-muted)" }}
              >
                {card.model_used.includes("haiku") ? "Haiku" : "Sonnet"}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function InsightCardSkeleton() {
  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{
        background: "var(--surface-raised)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <div className="skeleton h-5 w-28 rounded-full" />
      <div className="skeleton h-4 w-full rounded" />
      <div className="skeleton h-3 w-5/6 rounded" />
      <div className="skeleton h-3 w-3/4 rounded" />
    </div>
  );
}

// ── Exported component ────────────────────────────────────────────────────────

interface InsightCardsProps {
  insights: InsightCard[] | null | undefined;
  isLoading?: boolean;
  /** Maximum number of cards to display (default: 3) */
  maxCards?: number;
}

export default function InsightCards({
  insights,
  isLoading = false,
  maxCards = 3,
}: InsightCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <InsightCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!insights || insights.length === 0) {
    return null; // Don't render empty state — panel is optional
  }

  const displayed = insights.slice(0, maxCards);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {displayed.map((card) => (
        <InsightCardItem key={card.id} card={card} />
      ))}
    </div>
  );
}
